import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR, NgModel} from '@angular/forms';
import {ErrorStateMatcher} from '@angular/material/core';
import {ErrorStateMatcherFailure} from '../../services/material/errorStateMatcherFailure';
import {ErrorStateMatcherNoFailure} from '../../services/material/errorStateMatcherNoFailure';
import {of, Subject, Subscription} from 'rxjs';
import {StringUtils} from '../../services/utils/stringUtils';
import {Constants} from '../constants';
import {TranslationService} from '../../services/translation.service';
import {delay, switchMap, tap} from 'rxjs/operators';

@Component({
    selector: 'lv-material-form-input',
    templateUrl: './form-input.component.html',
    styleUrls: ['./form-input.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: forwardRef(() => FormInputComponent),
        }
    ],
    standalone: false
})

export class FormInputComponent implements ControlValueAccessor, OnChanges, OnDestroy {
  public static nextId = 0;
  @Input('required')
  public isRequired: boolean;

  @Input()
  public suffixTemplate: TemplateRef<any>;

  @Input('outerErrors')
  public outerErrors;

  @Input('hint')
  public hint;

  @Input('errorPath')
  public errorPath: string = '';

  @Input('textForRequiredError')
  public requiredErrorText: string;

  @Input('unit')
  public unit: string;

  @Input('icon')
  public icon: string;

  @Input('prefixIcon')
  public prefixIcon: string;

  @Input('pattern')
  public pattern: string;

  @Input('placeholder')
  public placeholder: string;

  @Input('maxlength')
  public maxlength: string | number | null;

  @Input('minlength')
  public minlength: string | number | null;

  @Input('disabled')
  public disabled: boolean;

  @Input('autoFocus')
  public autoFocus: boolean;

  @Input('selectAllOnFocus')
  public selectAllOnFocus: boolean;

  @Input('type')
  public type = 'text';

  @Input('id')
  public id = `lv-field-${FormInputComponent.nextId++}`;
  @Input('name')
  public name: string = '';
  @Input('stepsize')
  public stepsize = 1;

  @Input('autocomplete')
  public autocomplete: string;

  @Input()
  public meldung: any[];

  @Output()
  public inputChange: EventEmitter<any> = new EventEmitter<any>();

  @Output()
  public blurEvent: EventEmitter<any> = new EventEmitter<any>();

  @ViewChild('lvInputField', {static: true})
  private _inputField: NgModel;

  public stateMatcherFormInput: ErrorStateMatcher;
  public hasFocus: boolean;
  public showMeldung: boolean = false;
  public showUntruncatedError: boolean = false;
  public extendedHint: any;

  private _value: any;
  private _isDirty: boolean;
  private _isTouched: boolean;
  private _showFehlermeldung: boolean;
  private _showFehlermeldungSubject$ = new Subject<boolean>();
  private _fehlermeldungSubscription = Subscription.EMPTY;

  public constructor(private _translationService: TranslationService, private _cd: ChangeDetectorRef) {
    this._fehlermeldungSubscription = this._showFehlermeldungSubject$.pipe(
        switchMap(param => of(param).pipe(
            delay(3000),
            tap(_ => {
              this._showFehlermeldung = true;
              this.getError();
              this._cd.markForCheck();
            })))).subscribe();
  }
  public get isDirty(): boolean {
    return this._isDirty;
  }

  public get model(): any {
    return this._value;
  }

  @Input('ngModel')
  public set model(value: any) {
    if (!StringUtils.isNotNull(value) && !StringUtils.isNotNull(this._value)) {
      return;
    }
    const param = !!value ? value + '' : value;
    const model = !!this._value ? this._value + '' : this._value;
    if (param === model) {
      return;
    }
    this._value = value;
    this._onChange(this._value);
    this.inputChange.emit(this._value);
    //this._showFehlermeldungSubject$.next(false);
    this._showFehlermeldung = false;
    this._showFehlermeldungSubject$.next(true);
    //setTimeout(() => this._showFehlermeldungSubject$.next(true), 3000);
    this.showUntruncatedError = false;
  }

  @Input('isDirty')
  public set isDirty(value: boolean) {
    this._isDirty = value;
  }

  @Input('isTouched')
  public set isTouched(value: boolean) {
    this._isTouched = value;
  }
  setElementSelected(event): void {
    if (this.selectAllOnFocus) {
      event.target.select();
    }
  }
  public ngOnChanges(changes: SimpleChanges) {
    this.updateHint();
    this.getError();
  }

  public ngOnDestroy(): void {
    if (!!this._fehlermeldungSubscription) {
      this._fehlermeldungSubscription.unsubscribe();
    }
  }

  public isError(): boolean {
    return (this.isDirty || this._inputField.dirty || this._isTouched || this._inputField.touched)
        && (!!this._inputField.errors || !!this.outerErrors);
  }

  public getError(): string {
    this.stateMatcherFormInput = new ErrorStateMatcherFailure();
    const errorTrans = this.errorPath ? this.errorPath : 'INTEGER';
    if (this.isError() && this._inputField.errors?.required) {
      const requiredError = this._translationService.translate('ERRORS.REQUIRED');
      return !!this.requiredErrorText ? this.requiredErrorText : requiredError;
    }
    if (this.isError() && this.outerErrors?.PARTIAL) {
      const partialError = this._translationService.translate('ERRORS.' + errorTrans + '.' + this.outerErrors.PARTIAL, {limit: this.outerErrors.VIOLATION});
      return (!this.hasFocus || this._showFehlermeldung) ? partialError : ' ';
    }
    if (this.isError() && this.outerErrors?.ERROR) {
      return this._translationService.translate('ERRORS.' + errorTrans + '.' + this.outerErrors.ERROR);
    }
    this._meldungAnzeigen();
    if (this.showMeldung) {
      return this.meldung[0].Meldungstext;
    }
    this.stateMatcherFormInput = new ErrorStateMatcherNoFailure();
    return null;
  }

  public onBlur(): void {
    this.hasFocus = false;
    this.blurEvent.emit(this._value);
    this.getError();
  }

  public onErrorClick() {
    this.showUntruncatedError = !this.showUntruncatedError;
  }

  public writeValue(value: any): void {
    this._value = value;
  }

  public registerOnChange(fn: (_: any) => void): void {
    this._onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  private _onChange = (_: any) => void 0;
  private _onTouched = () => void 0;

  private updateHint(){
    if(!!this.maxlength && `${this._value}`.length >= +this.maxlength) {
      this.extendedHint = !!this.hint ?
          this._translationService.translate('ERRORS.INPUT.MAXIMUM_CHARACTERS', {x: this.maxlength}) + '<br>' + this.hint :
          this._translationService.translate('ERRORS.INPUT.MAXIMUM_CHARACTERS', {x: this.maxlength});
    } else if(!!this.minlength && `${this._value}`.length < +this.minlength){
      this.extendedHint = !!this.hint ?
          this._translationService.translate('ERRORS.INPUT.MINIMUM_CHARACTERS', {x: this.minlength}) + '<br>' + this.hint :
          this._translationService.translate('ERRORS.INPUT.MINIMUM_CHARACTERS', {x: this.minlength});
    } else {
      this.extendedHint = this.hint;
    }
  }

  private _meldungAnzeigen() {
    let show = false;
    switch (Constants.SHOW_MELDUNG_STATE) {
      case 'onTouched':
        show = !!this.meldung?.length && (this._isTouched || this._inputField.touched);
        break;
      case 'onDirty':
        show = !!this.meldung?.length && (this._isDirty || this._inputField.dirty);
        break;
      default:
        show = !!this.meldung?.length;
    }
    this.showMeldung = show;
  }
}
